/**
 * AR Technical Demo (F1 / GEN-2257) — Scene 1 inbound-remittance email viewer.
 *
 * THROWAWAY DEMO CODE. Renders the RA-7741 remittance-advice email that arrived
 * from Crestline Markets plus a preview of the attached remittance PDF (the
 * static `remittance-ra7741.svg` asset). It is fed entirely from the fixture
 * store's `inboundEmailsById` slice (the full provenance copy of the source
 * email, which carries the body + attachment) and renders with ZERO backend
 * calls.
 *
 * Why bespoke instead of `SmartEmailViewer`: the real remittance page embeds
 * `SmartEmailViewer`, which switches to `EmailThreadViewer` whenever the source
 * email has a `conversationId` — and that fetches the conversation over RPC,
 * which the demo can't serve. This deterministic panel mirrors the proven
 * `DemoSentEmail` (Scene 2 of the vision demo) so the scene always looks correct
 * on stage. Mounted only from `RemittanceDetails` behind an `isArDemoMode()`
 * guard for the demo remittance. Delete with `src/demo/ar`.
 */

import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { format } from 'date-fns';
import { EnvelopeSimpleIcon, FilePdfIcon } from '@phosphor-icons/react';
import { useArDemoStore } from './fixture-store';

function formatReceived(
  value: Date | string | null | undefined,
): string | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return format(d, 'MMM d, yyyy, h:mm a');
}

/**
 * The Scene-1 inbound remittance-advice email + attached-PDF preview, read from
 * the fixture store by the source inbound-email id stored on the remittance.
 */
export function DemoRemittanceEmailPanel({
  inboundEmailId,
}: {
  inboundEmailId: string;
}) {
  const gt = useGT();
  const [imageError, setImageError] = useState(false);
  const email = useArDemoStore((s) => s.inboundEmailsById[inboundEmailId]);

  if (!email) {
    return (
      <div className="flex h-full items-center justify-center p-4 text-sm text-muted-foreground">
        <T context="AR demo: remittance source email missing">
          No inbound email to display
        </T>
      </div>
    );
  }

  const pdfAttachment = email.attachments?.find(
    (a) =>
      a.mimeType === 'application/pdf' ||
      a.fileName?.toLowerCase().endsWith('.pdf'),
  );
  const receivedAt = formatReceived(email.createdAt);
  // Bind non-translatable values (addresses, filename) to locals so they are
  // not raw JSX string literals (i18n jsx-only mode flags those).
  const fromAddress = email.from ?? '';
  const toAddress = email.to;
  const bodyText =
    email.textContent ??
    'Please find attached our remittance covering the listed invoices.';

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto px-6 py-6">
      <div className="flex items-center gap-2">
        <EnvelopeSimpleIcon className="h-5 w-5 text-muted-foreground" />
        <h3 className="text-sm font-semibold">
          {email.subject ??
            gt('Remittance', {
              context: 'AR demo: remittance email subject fallback',
            })}
        </h3>
      </div>

      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        <span className="text-muted-foreground">
          <T>From</T>
        </span>
        <span className="truncate">{fromAddress}</span>
        <span className="text-muted-foreground">
          <T>To</T>
        </span>
        <span className="truncate">{toAddress}</span>
        {receivedAt && (
          <>
            <span className="text-muted-foreground">
              <T context="AR demo: inbound email received timestamp label">
                Received
              </T>
            </span>
            <span>{receivedAt}</span>
          </>
        )}
      </div>

      <p className="text-sm text-foreground">{bodyText}</p>

      {pdfAttachment && (
        <div className="rounded border bg-muted/30">
          <div className="flex items-center gap-2 border-b px-3 py-2 text-xs font-medium text-muted-foreground">
            <FilePdfIcon className="size-4" />
            <span>{pdfAttachment.fileName}</span>
            <span className="ml-auto">
              <T context="AR demo: remittance attachment label">Remittance</T>
            </span>
          </div>
          <div className="flex justify-center bg-white p-4">
            {imageError ? (
              <div className="py-12 text-sm text-muted-foreground">
                <T context="AR demo: remittance PDF preview unavailable">
                  Remittance preview unavailable
                </T>
              </div>
            ) : (
              <img
                src={pdfAttachment.s3Url}
                alt={gt('Remittance document', {
                  context: 'AR demo remittance PDF image alt text',
                })}
                className="max-w-full rounded border shadow-sm"
                onError={() => setImageError(true)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
